import styled from '@emotion/styled';
import { BaseButton, Variables } from '../../base';

export const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const SectionLabel = styled.div`
    font-size: 12px;
    font-weight: ${Variables.FontWeight.semibold};
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextMild};
`;

export const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const Hint = styled.div`
    display: flex;
    gap: 7px;
    align-items: flex-start;
    font-size: 12px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextMedium};
`;

export const EmrCard = styled.div`
    background: ${Variables.ThemeLight.PrimaryOpacity3};
    border: 1px solid ${Variables.ThemeLight.PrimaryInverted7};
    border-radius: 8px;
    color: ${Variables.ThemeLight.TextMedium};
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const ResumeCard = styled.div`
    background: ${Variables.ThemeLight.AttentionOpacity3};
    border: 1px solid ${Variables.ThemeLight.AttentionInverted8};
    border-radius: 8px;
    color: ${Variables.ThemeLight.TextMedium};
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const EditCard = styled.div`
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: 8px;
    background: ${Variables.ThemeLight.Background3};
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const UploadDropZone = styled.div<{ $isDragging?: boolean; $disabled?: boolean }>`
    border: 1.5px dashed
        ${p =>
            p.$isDragging
                ? Variables.ThemeLight.PrimaryConstant6
                : Variables.ThemeLight.PrimaryInverted7};
    background: ${p =>
        p.$disabled
            ? Variables.ThemeLight.NeutralMild1
            : p.$isDragging
              ? Variables.ThemeLight.PrimaryOpacity2
              : 'transparent'};
    border-radius: 8px;
    padding: 18px 16px;
    text-align: center;
    cursor: ${p => (p.$disabled ? 'default' : 'pointer')};
    transition:
        background 120ms ease-out,
        border-color 120ms ease-out;
`;

export const UploadDropTitle = styled.div`
    font-size: 14px;
    font-weight: ${Variables.FontWeight.medium};
    line-height: 1.3;
    color: ${Variables.ThemeLight.TextStrong};
    margin-top: 6px;
`;

export const UploadDropHint = styled.div`
    font-size: 12px;
    font-weight: ${Variables.FontWeight.medium};
    color: ${Variables.ThemeLight.TextMild};
    margin-top: 2px;
`;

export const UploadHiddenInput = styled.input`
    display: none;
`;

export const CardHeader = styled.div`
    display: flex;
    gap: 9px;
    align-items: flex-start;
`;

export const CardTitle = styled.span`
    display: block;
    font-size: 13px;
    font-weight: ${Variables.FontWeight.semibold};
    line-height: 1.2;
    color: ${Variables.ThemeLight.PrimaryInverted4};
`;

export const EmrCardTitle = styled(CardTitle)`
    color: ${Variables.ThemeLight.PrimaryConstant6};
`;

export const ResumeCardTitle = styled(CardTitle)`
    color: ${Variables.ThemeLight.AttentionInverted4};
`;

export const CardSub = styled.span`
    display: block;
    font-size: 12px;
    font-weight: ${Variables.FontWeight.medium};
    line-height: 1.35;
    color: ${Variables.ThemeLight.TextMedium};
    margin-top: 3px;
`;

export const Actions = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
`;

export const PrimaryButton = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    flex: none;
    min-width: unset;
    font-size: 13px;
    font-weight: ${Variables.FontWeight.semibold};
    color: ${Variables.Colors.white};
    background: ${Variables.ThemeLight.PrimaryConstant6};
    background-color: ${Variables.ThemeLight.PrimaryConstant6};
    border: none;
    border-radius: 6px;
    padding: 0 14px;
    height: 38px;
    cursor: pointer;

    svg {
        fill: ${Variables.Colors.white};
    }

    &:disabled {
        background: ${Variables.ThemeLight.NeutralMild1};
        background-color: ${Variables.ThemeLight.NeutralMild1};
        color: ${Variables.Colors.white};
        cursor: default;

        svg {
            fill: ${Variables.Colors.white};
        }
    }

    &:not(:disabled):hover {
        opacity: 0.92;
        background-color: ${Variables.ThemeLight.PrimaryConstant6};
    }
`;

export const AttentionButton = styled(PrimaryButton)`
    color: ${Variables.ThemeLight.AttentionInverted4};
    background: ${Variables.ThemeLight.Background2};
    background-color: ${Variables.ThemeLight.Background2};
    border: 1px solid ${Variables.ThemeLight.AttentionInverted8};

    svg {
        fill: ${Variables.ThemeLight.AttentionInverted4};
    }

    &:not(:disabled):hover {
        opacity: 0.92;
        background: ${Variables.Colors.white};
        background-color: ${Variables.Colors.white};
    }
`;

export const Chooser = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: 8px;
    background: ${Variables.ThemeLight.Background3};
    overflow: hidden;
`;

export const ChooserOption = styled(BaseButton)`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-width: unset;
    justify-content: flex-start;
    text-align: left;
    background: transparent;
    background-color: transparent;
    border: none;
    border-radius: 0;
    border-bottom: 1px solid ${Variables.ThemeLight.Border2};
    padding: 10px 12px;
    height: auto;
    cursor: pointer;
    font-size: 13px;
    font-weight: ${Variables.FontWeight.medium};
    color: ${Variables.ThemeLight.TextStrong};

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: ${Variables.ThemeLight.NeutralMild1};
        background-color: ${Variables.ThemeLight.NeutralMild1};
    }
`;

export const ChooserId = styled.span`
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 12px;
    color: ${Variables.ThemeLight.TextMedium};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const SuccessCard = styled.div`
    border: 1px solid ${Variables.ThemeLight.SuccessInverted7};
    border-radius: 8px;
    background: ${Variables.ThemeLight.SuccessOpacity3};
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const SuccessTitle = styled.span`
    display: block;
    font-size: 14px;
    font-weight: ${Variables.FontWeight.semibold};
    line-height: 1.25;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const SuccessBody = styled.span`
    display: block;
    font-size: 12px;
    font-weight: ${Variables.FontWeight.medium};
    line-height: 1.45;
    color: ${Variables.ThemeLight.TextMedium};
    margin-top: 4px;
`;
